import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import jwtDecode from "jwt-decode";
import instanceAuth from "services/auth";

const AuthContext = React.createContext();

export const AuthContextProvider = ({ enabled, children }) => {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    if (enabled) {
      instanceAuth.getUser().then(userR => {
        setUser(userR);
      });
      instanceAuth.getEvents().addUserLoaded(userR => {
        setUser(userR);
      });
    }
  }, [enabled]);

  const value = React.useMemo(() => {
    const logout = async () => {
      await instanceAuth.logout();
    };

    const signoutRedirectCallback = async () => {
      return instanceAuth.signoutRedirectCallback();
    };

    const login = async () => {
      return instanceAuth.login();
    };

    const completeLoginVistoriador = async (data, cpf, email) => {
      return instanceAuth.completeLoginVistoriador(data, cpf, email);
    };

    const completeLoginVistoriadorCohab = async (
      data,
      cpf,
      email,
      nome,
      numeroOcorrencia
    ) => {
      return instanceAuth.completeLoginVistoriadorCohab(
        data,
        cpf,
        email,
        nome,
        numeroOcorrencia
      );
    };

    const completeLoginEC = async (data, cpf, email) => {
      return instanceAuth.completeLoginEC(data, cpf, email);
    };

    const clearLocalStorageSoft = async data => {
      return instanceAuth.clearLocalStorageSoft(data);
    };

    const loginCallback = async () =>
      new Promise((go, rej) => {
        instanceAuth
          .loginCallback()
          .then(userR => {
            go();
            setUser(userR);
          })
          .catch(err => {
            rej(err);
          });
      });

    const { renewCallback } = instanceAuth;

    const verifyLogin = async () => {
      const userR = await instanceAuth.getUser();
      return !!userR && !!userR.id_token && !userR.expired;
    };

    const getUser = () => instanceAuth.getUser();

    const clear = () => instanceAuth.clear();

    const renewSoftAuth = async () => {
      return instanceAuth.renewSoftAuth();
    };

    const setUserWrapper = data => {
      // eslint-disable-next-line no-shadow
      setUser(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(data)) {
          return data;
        }
        return prev;
      });
    };

    return {
      login,
      logout,
      user,
      getUser,
      verifyLogin,
      loginCallback,
      renewCallback,
      signoutRedirectCallback,
      clear,
      completeLoginVistoriador,
      completeLoginVistoriadorCohab,
      completeLoginEC,
      clearLocalStorageSoft,
      setUser: setUserWrapper,
      renewSoftAuth
    };
  }, [user]);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (user && user.isSoftauth && (user.va || user.ec)) {
        if (user.access_token) {
          try {
            const decoded = jwtDecode(user.access_token);
            const now = Math.floor(Date.now() / 1000);
            if (
              decoded.exp &&
              decoded.iat &&
              now >= decoded.iat &&
              decoded.exp - now < 300
            ) {
              console.warn("Renovando token...");
              const newUser = await instanceAuth.renewSoftAuth();
              if (newUser) {
                setUser(newUser);
              }
            }
          } catch (error) {
            console.error("Failed to check/renew soft auth token", error);
          }
        }
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthContextProvider.propTypes = {
  enabled: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired
};

export default AuthContext;
